import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { FxAlert, FxButton, FxIcon, FxSpinner } from '@/components/fx'
import { uploadLinksApi } from '@/lib/api'
import { useCockpitUploadLinks } from '@/pages/cockpit/useCockpit'
import type { UploadLink } from '@/types/domain'

import { QrCode } from './QrCode'
import {
  UPLOAD_LINK_STATE_LABELS,
  formatDate,
  isEmailUnverified,
  sortedLinks,
  uploadLinkState,
} from './shareModel'

/**
 * Per Handy hinzufügen: the links a phone opens, with no account, to
 * photograph forms straight into this template.
 *
 * Three things about them the design does not show and a person needs to be
 * told, because each one has a cost that cannot be undone:
 *  - a link is a standing invitation to upload into this account, so minting
 *    one needs a confirmed address (the gateway refuses with
 *    `email_unverified`, and without the grace window every other upload path
 *    gets);
 *  - revoking is immediate and permanent — a QR already printed and taped to
 *    a folder stops working, and the row stays revoked for ever;
 *  - the address in the QR code is a real address. In development the
 *    gateway composes it from the production capture site, so the link is
 *    shown as text under the code and flagged when this build has no
 *    `VITE_PUBLIC_UPLOAD_BASE_URL` of its own.
 */
export function UploadLinkSection({
  templateId,
  canWrite,
}: {
  templateId: string
  canWrite: boolean
}) {
  const queryClient = useQueryClient()
  const links = useCockpitUploadLinks(templateId, canWrite)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['uploadLinks', templateId] })

  const [expiresInDays, setExpiresInDays] = useState('30')
  const create = useMutation({
    mutationFn: () => {
      const days = Number(expiresInDays)
      return uploadLinksApi.create(templateId, days > 0 ? { expiresInDays: days } : {})
    },
    onSuccess: () => void refresh(),
  })
  const revoke = useMutation({
    mutationFn: (token: string) => uploadLinksApi.revoke(templateId, token),
    onSuccess: () => void refresh(),
  })

  if (!canWrite) {
    return (
      <Section>
        <p className="m-0 text-small text-text-muted">
          Handy-Links kann nur anlegen, wer dieses Formular bearbeiten darf.
        </p>
      </Section>
    )
  }

  const rows = sortedLinks(links.links)
  const createError = create.error

  return (
    <Section>
      {links.isPending && <FxSpinner label="Handy-Links werden geladen …" />}

      {links.forbidden && (
        <p className="m-0 text-small text-text-muted">
          Handy-Links kann nur anlegen, wer dieses Formular bearbeiten darf.
        </p>
      )}

      {links.isError && !links.forbidden && (
        <FxAlert tone="danger">Die Handy-Links konnten nicht geladen werden.</FxAlert>
      )}

      {createError && isEmailUnverified(createError) && (
        <FxAlert tone="info" title="Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse">
          Ein Handy-Link lässt jede Person, die ihn hat, Dokumente in Ihr Konto hochladen. Er
          lässt sich deshalb erst anlegen, wenn Ihre Adresse bestätigt ist — anders als beim
          Hochladen gibt es dafür keine Übergangsfrist. Bereits angelegte Links funktionieren,
          bis die Frist zum Bestätigen abgelaufen ist.
        </FxAlert>
      )}
      {createError && !isEmailUnverified(createError) && (
        <FxAlert tone="danger">Der Handy-Link konnte nicht angelegt werden.</FxAlert>
      )}
      {revoke.isError && <FxAlert tone="danger">Der Link wurde nicht zurückgezogen.</FxAlert>}

      {links.isSuccess && rows.length === 0 && (
        <p className="m-0 text-small text-text-muted">
          Noch kein Handy-Link. Legen Sie einen an, scannen Sie den QR-Code mit dem Handy und
          fotografieren Sie Formulare direkt in dieses Musterformular.
        </p>
      )}

      {rows.map((link) => (
        <LinkRow
          key={link.token}
          link={link}
          busy={revoke.isPending}
          onRevoke={() => revoke.mutate(link.token)}
        />
      ))}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-small">Gültig</span>
          <select
            className="h-control rounded-md bg-surface px-3 text-body text-text shadow-raised"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(event.target.value)}
          >
            <option value="7">7 Tage</option>
            <option value="30">30 Tage</option>
            <option value="0">unbegrenzt</option>
          </select>
        </label>
        <FxButton
          variant="tonal"
          icon="qr_code_2"
          disabled={create.isPending}
          onClick={() => create.mutate()}
        >
          Handy-Link anlegen
        </FxButton>
      </div>
    </Section>
  )
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-t-2 border-divider pt-4">
      <h3 className="m-0 text-panel-title">Per Handy hinzufügen</h3>
      {children}
    </section>
  )
}

/** Set when this build points its links somewhere other than the gateway's
 *  own base URL. Unset in development means the QR code opens production. */
const OVERRIDE = import.meta.env.VITE_PUBLIC_UPLOAD_BASE_URL as string | undefined

function LinkRow({
  link,
  busy,
  onRevoke,
}: {
  link: UploadLink
  busy: boolean
  onRevoke: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [copied, setCopied] = useState(false)
  const state = uploadLinkState(link)
  const url = link.url ?? ''

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // A browser that refuses the clipboard (no permission, no secure
      // context) still shows the address under the code to copy by hand.
      setCopied(false)
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-4 rounded-sm bg-ground p-4">
      {state === 'active' && url !== '' && (
        <div className="flex-none rounded-sm bg-primary-subtle p-2">
          <QrCode
            value={url}
            label={`QR-Code für den Handy-Upload in dieses Musterformular: ${url}`}
            className="h-[120px] w-[120px]"
          />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="flex flex-wrap items-center gap-2 text-small">
          <span className="rounded-sm bg-fill px-2 py-0.5">
            {UPLOAD_LINK_STATE_LABELS[state]}
          </span>
          <span>
            angelegt {formatDate(link.createdAt)}
            {link.expiresAt ? ` · gültig bis ${formatDate(link.expiresAt)}` : ' · ohne Ablauf'}
          </span>
          <span>· {link.uploads} Uploads</span>
        </span>

        {url !== '' && <span className="break-all text-small text-text-muted">{url}</span>}

        {import.meta.env.DEV && !OVERRIDE && (
          <span className="text-small text-warning-text">
            Achtung: Dieser Link zeigt auf die Produktivumgebung. Setzen Sie
            VITE_PUBLIC_UPLOAD_BASE_URL, bevor Sie den Code am Arbeitsplatz scannen.
          </span>
        )}

        {state === 'active' && (
          <div className="flex flex-wrap items-center gap-3">
            <FxButton variant="tonal" className="h-9 text-small" icon="link" onClick={() => void copy()}>
              {copied ? 'Kopiert' : 'Link kopieren'}
            </FxButton>

            {confirming ? (
              <span className="flex flex-wrap items-center gap-2 text-small">
                <span>
                  Zurückziehen lässt sich nicht rückgängig machen — ein bereits gedruckter
                  QR-Code funktioniert danach nicht mehr.
                </span>
                <FxButton
                  variant="dangerTonal"
                  className="h-9 text-small"
                  disabled={busy}
                  onClick={() => {
                    setConfirming(false)
                    onRevoke()
                  }}
                >
                  Zurückziehen
                </FxButton>
                <FxButton
                  variant="outlined"
                  className="h-9 text-small"
                  onClick={() => setConfirming(false)}
                >
                  Abbrechen
                </FxButton>
              </span>
            ) : (
              <button
                type="button"
                className="flex items-center gap-1 rounded-sm px-2 py-1 text-small font-semibold text-danger-text"
                onClick={() => setConfirming(true)}
              >
                <FxIcon name="link_off" size={18} />
                Link zurückziehen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
